/**
 * Shared UI primitives for expandable index-page detail panels
 * (see docs/expandable-index-pages.md). Field/action parity and page-specific
 * data live in each resource folder; these are the layout building blocks.
 */
import { useNotify } from 'react-admin';
import {
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const MONO = "'SFMono-Regular', Consolas, 'Liberation Mono', monospace";

/** Uppercase, tracked section header — same voice as the theme's table headers. */
export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="div"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

interface DetailRowProps {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  hide?: boolean;
  /** When set (and value non-empty), the value renders as a filter link. */
  onClick?: () => void;
  title?: string;
}

/** One aligned label/value row (130px label grid). */
export const DetailRow = ({ label, value, mono, hide, onClick, title }: DetailRowProps) => {
  if (hide) return null;
  const display = value === null || value === undefined || value === '' ? '—' : value;
  const isEmpty = display === '—';
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 1, py: 0.375 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
        {label}
      </Typography>
      {onClick && !isEmpty ? (
        <Link
          component="button"
          type="button"
          onClick={onClick}
          underline="hover"
          title="Filter the list by this value"
          sx={{
            fontSize: 13,
            fontFamily: mono ? MONO : undefined,
            textAlign: 'left',
            justifySelf: 'start',
            color: 'primary.dark',
          }}
        >
          {display}
        </Link>
      ) : (
        <Typography
          variant="body2"
          title={title}
          sx={{
            fontFamily: mono ? MONO : undefined,
            fontSize: mono ? 12.5 : 13,
            color: isEmpty ? 'text.disabled' : 'text.primary',
            wordBreak: 'break-word',
          }}
        >
          {display}
        </Typography>
      )}
    </Box>
  );
};

export type DotTone = 'success' | 'error' | 'warning' | 'neutral';

const DOT_COLOR: Record<DotTone, string> = {
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
  neutral: 'grey.400',
};

/** Colored status dot + text, for eligibility / status-style values. */
export const StatusDot = ({ value, tone }: { value: React.ReactNode, tone: DotTone }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DOT_COLOR[tone], flexShrink: 0 }} />
    <Typography variant="body2" sx={{ fontSize: 13 }}>
      {value ?? '—'}
    </Typography>
  </Stack>
);

export interface KeyFact {
  label: string;
  value: React.ReactNode;
}

/** Horizontal strip of the handful of values a user scans for first. */
export const KeyFactsStrip = ({ facts }: { facts: KeyFact[] }) => (
  <Stack
    direction="row"
    sx={{
      bgcolor: 'grey.50',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 2,
      py: 1.25,
      flexWrap: 'wrap',
      rowGap: 1.5,
    }}
    divider={<Box sx={{ width: '1px', bgcolor: 'divider', mx: 2 }} />}
  >
    {facts.map(({ label, value }) => (
      <Box key={label} sx={{ minWidth: 90 }}>
        <Typography sx={{ fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      </Box>
    ))}
  </Stack>
);

export interface Flag {
  label: string;
  color: 'warning' | 'error' | 'default';
  show: boolean;
  title?: string;
}

/** Boolean/warning flag chips — rendered only when true. */
export const FlagChips = ({ flags }: { flags: Flag[] }) => {
  const visible = flags.filter(flag => flag.show);
  if (!visible.length) return null;
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {visible.map(({ label, color, title }) => (
        <Tooltip key={label} title={title ?? ''}>
          <Chip
            size="small"
            color={color === 'default' ? undefined : color}
            variant={color === 'default' ? 'outlined' : 'filled'}
            label={label}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

/** Copy-to-clipboard icon button with a toast. */
export const CopyButton = ({ name, value }: { name: string, value: string }) => {
  const notify = useNotify();
  return (
    <IconButton
      size="small"
      aria-label={`Copy ${name}`}
      sx={{ p: 0.25, ml: 0.25 }}
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        notify(`${name} copied`, { type: 'info' });
      }}
    >
      <ContentCopyIcon sx={{ fontSize: 13 }} />
    </IconButton>
  );
};
